import { supabase } from '../lib/supabase';
import salesforceService from './salesforceService';

/**
 * Lead Service - Handles all lead-related operations with Supabase
 * Includes snake_case ↔ camelCase conversion
 * CRITICAL: Always uses authenticated user's ID for security
 */

// Convert database snake_case to React camelCase
const convertLeadFromDb = (lead) => {
  if (!lead) return null;
  
  return {
    id: lead?.id,
    partnerId: lead?.partner_id,
    customerName: lead?.customer_name,
    email: lead?.email,
    phone: lead?.phone,
    propertyType: lead?.property_type,
    homeOwner: lead?.home_owner__c,
    propertyAddress: lead?.property_address,
    propertyCity: lead?.property_city,
    propertyState: lead?.property_state,
    propertyZip: lead?.property_zip,
    referralType: lead?.referral_type,
    referralNotes: lead?.referral_notes,
    status: lead?.status,
    salesforceId: lead?.salesforce_id,
    referringAccountId: lead?.referring_account__c,
    referredById: lead?.referred_by__c,
    createdAt: lead?.created_at,
    updatedAt: lead?.updated_at
  };
};

// Convert React camelCase to database snake_case
const convertLeadToDb = (lead) => {
  return {
    partner_id: lead?.partnerId,
    customer_name: lead?.customerName,
    email: lead?.email,
    phone: lead?.phone,
    property_type: lead?.propertyType,
    home_owner__c: lead?.homeOwner,
    property_address: lead?.propertyAddress,
    property_city: lead?.propertyCity,
    property_state: lead?.propertyState,
    property_zip: lead?.propertyZip,
    referral_type: lead?.referralType,
    referral_notes: lead?.referralNotes,
    status: lead?.status,
    salesforce_id: lead?.salesforceId,
    referring_account__c: lead?.referringAccountId,
    referred_by__c: lead?.referredById
  };
};

export const leadService = {
  /**
   * Get all leads for the authenticated user
   * RLS policy automatically filters to user's leads only
   */
  async getAllLeads() {
    try {
      // CRITICAL: Wait for session to be ready
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('leads')?.select('*')?.eq('partner_id', session?.user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || [])?.map(convertLeadFromDb);
    } catch (error) {
      console.error('Error fetching leads:', error);
      throw error;
    }
  },

  /**
   * Get a single lead by ID
   * RLS policy ensures user can only access their own leads
   */
  async getLeadById(id) {
    try {
      // CRITICAL: Wait for session to be ready
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('leads')?.select('*')?.eq('id', id)?.eq('partner_id', session?.user?.id)?.single();

      if (error) throw error;
      
      return convertLeadFromDb(data);
    } catch (error) {
      console.error('Error fetching lead:', error);
      throw error;
    }
  },

  /**
   * Create a new lead
   * @param {Object} leadData - Lead information
   * @returns {Promise<Object>} Created lead data
   */
  async createLead(leadData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User must be authenticated to create leads');
    }

    // Fetch user profile to get Salesforce IDs
    const { data: userProfile, error: profileError } = await supabase
      ?.from('user_profiles')?.select('salesforce_account_id, salesforce_contact_id')?.eq('id', user?.id)
      ?.single();

    if (profileError) {
      console.warn('⚠️ Could not fetch user profile:', profileError);
    }

    // Insert lead into database with all fields including home_owner__c
    const { data, error } = await supabase?.from('leads')?.insert([
        {
          partner_id: user?.id,
          customer_name: leadData?.customerName,
          email: leadData?.email,
          phone: leadData?.phone,
          property_address: leadData?.propertyAddress,
          property_city: leadData?.propertyCity,
          property_state: leadData?.propertyState,
          property_zip: leadData?.propertyZip,
          property_type: leadData?.propertyType,
          home_owner__c: leadData?.homeOwner,
          referral_type: leadData?.referralType,
          referral_notes: leadData?.referralNotes,
          status: 'Pending',
          salesforce_id: null,
          referring_account__c: userProfile?.salesforce_account_id || null,
          referred_by__c: userProfile?.salesforce_contact_id || null
        }
      ])?.select()?.single();

    if (error) {
      console.error('Error creating lead:', error);
      throw new Error(`Failed to create lead: ${error.message}`);
    }

    // Try to sync with Salesforce (non-blocking)
    let salesforceResult = null;
    try {
      console.log('🔄 Attempting Salesforce sync...');
      
      // Include all fields including homeOwner in Salesforce sync
      const salesforceLeadData = {
        ...leadData,
        partnerId: user?.id,
        referringAccountId: userProfile?.salesforce_account_id,
        referredById: userProfile?.salesforce_contact_id
      };
      
      salesforceResult = await salesforceService?.createLead(salesforceLeadData, data?.id);
      
      // Update lead with Salesforce ID if sync was successful
      if (salesforceResult?.success && salesforceResult?.salesforceId) {
        await supabase?.from('leads')?.update({ salesforce_id: salesforceResult?.salesforceId })?.eq('id', data?.id);
        
        console.log('✅ Lead synced to Salesforce:', salesforceResult?.salesforceId);
      } else if (salesforceResult?.skipped) {
        console.log('⏭️ Salesforce sync skipped:', salesforceResult?.error);
      } else {
        console.warn('⚠️ Salesforce sync failed:', salesforceResult?.error);
      }
    } catch (salesforceError) {
      console.warn('⚠️ Salesforce sync error:', salesforceError?.message);
      salesforceResult = {
        success: false,
        error: salesforceError?.message
      };
    }

    return {
      ...data,
      salesforceSync: salesforceResult
    };
  },

  /**
   * Save lead as draft
   * Creates a new draft lead or updates existing draft
   * Status is automatically set to 'Draft'
   */
  async saveDraft(leadData, draftId = null) {
    try {
      // CRITICAL: Wait for session to be ready
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const dbLead = convertLeadToDb({
        ...leadData,
        partnerId: session?.user?.id,
        status: 'Draft' // Always set status to Draft
      });

      if (draftId) {
        // Update existing draft
        const { data, error } = await supabase
          ?.from('leads')?.update(dbLead)?.eq('id', draftId)?.eq('partner_id', session?.user?.id)?.eq('status', 'Draft') // Only update if it's still a draft
          ?.select()
          ?.single();
        
        if (error) throw error;
        
        return convertLeadFromDb(data);
      } else {
        // Create new draft
        const { data, error } = await supabase
          ?.from('leads')
          ?.insert(dbLead)
          ?.select()
          ?.single();
        
        if (error) throw error;
        
        return convertLeadFromDb(data);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  },

  /**
   * Get all draft leads for the authenticated user
   */
  async getDraftLeads() {
    try {
      // CRITICAL: Wait for session to be ready
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('leads')?.select('*')?.eq('partner_id', session?.user?.id)?.eq('status', 'Draft')?.order('updated_at', { ascending: false });

      if (error) throw error;
      
      return (data || [])?.map(convertLeadFromDb);
    } catch (error) {
      console.error('Error fetching draft leads:', error);
      throw error;
    }
  },

  /**
   * Update an existing lead
   * RLS policy ensures user can only update their own leads
   */
  async updateLead(id, leadData) {
    try {
      // CRITICAL: Wait for session to be ready
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const dbLead = convertLeadToDb(leadData);

      const { data, error } = await supabase?.from('leads')?.update(dbLead)?.eq('id', id)?.eq('partner_id', session?.user?.id)?.select()?.single();

      if (error) throw error;
      
      return convertLeadFromDb(data);
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  },

  /**
   * Delete a lead
   * RLS policy ensures user can only delete their own leads
   */
  async deleteLead(id) {
    try {
      // CRITICAL: Wait for session to be ready
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('leads')?.delete()?.eq('id', id)?.eq('partner_id', session?.user?.id);

      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  },

  /**
   * Get lead statistics for the authenticated user
   */
  async getLeadStats() {
    try {
      // CRITICAL: Wait for session to be ready
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      // Fetch user profile to get custom reward amounts
      const { data: userProfile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('cesspool_conversion_reward_amount, other_referral_reward_amount')
        ?.eq('id', session?.user?.id)
        ?.single();

      if (profileError) {
        console.warn('⚠️ Could not fetch user profile for reward amounts:', profileError);
      }

      const { data, error } = await supabase?.from('leads')?.select('status, created_at, referral_type')?.eq('partner_id', session?.user?.id);

      if (error) throw error;

      const leads = data || [];
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // Calculate potential rewards based on referral types
      const cesspoolConversionAmount = userProfile?.cesspool_conversion_reward_amount || 500;
      const otherReferralAmount = userProfile?.other_referral_reward_amount || 50;

      const cesspoolLeads = leads?.filter(l => l?.referral_type === 'Cesspool Conversion');
      const otherLeads = leads?.filter(l => l?.referral_type === 'Septic Maintenance' || l?.referral_type === 'Other');

      const potentialRewards = (cesspoolLeads?.length * cesspoolConversionAmount) + (otherLeads?.length * otherReferralAmount);

      const stats = {
        totalLeads: leads?.length,
        pendingLeads: leads?.filter(l => l?.status === 'Pending')?.length,
        qualifiedLeads: leads?.filter(l => l?.status === 'Qualified')?.length,
        closedLeads: leads?.filter(l => l?.status === 'Closed Won' || l?.status === 'Closed Lost')?.length,
        monthlySubmissions: leads?.filter(l => new Date(l.created_at) >= thisMonth)?.length,
        potentialRewards: potentialRewards
      };

      return stats;
    } catch (error) {
      console.error('Error fetching lead stats:', error);
      throw error;
    }
  }
};